export interface Locator {
  pattern: string
  replacement: string
}

export interface ErrorReportingConfig {
  apiKey: string
  environment: string
  revision: string
  locators: Locator[]
  userId?: number
  tenantId?: number
}

export interface ScrubbableNotice {
  url?: string | undefined
  message: string
  stack: string
  cause?: object | undefined
  cookies: Record<string, unknown> | string
  params: Record<string, unknown>
  cgiData: Record<string, unknown>
}

export type ScrubbedCause = {
  name: string
  message: string
  stack: string
  cause?: ScrubbedCause | undefined
}

export interface ScrubbableBreadcrumb {
  category: string
  message: string
  metadata: Record<string, unknown>
}

const FILTERED = '[FILTERED]'
const EMAIL_ADDRESS = /[\w.+%-]+@[\w-]+(?:\.[\w-]+)+/g
const ORIGIN_AND_PATH = /^((?:[a-z][a-z0-9+.-]*:)?\/\/[^/]*)?(.*)$/is
const MAX_CAUSES = 3
const RESIZE_LOOP_WARNING = 'ResizeObserver loop'
const BREADCRUMB_CATEGORIES = new Set(['navigation', 'request', 'error', 'notice'])
const BREADCRUMB_URLS = new Set(['url', 'from', 'to'])
const BREADCRUMB_TEXTS = new Set(['name', 'message', 'stack'])
const BREADCRUMB_SCALARS = new Set(['type', 'method', 'status_code'])

export function parseErrorReportingConfig(
  content: string | null | undefined,
): ErrorReportingConfig | null {
  if (!content) return null
  return JSON.parse(content) as ErrorReportingConfig
}

export function isBrowserNoise(message: string, documentUrl: string): boolean {
  return message.startsWith(RESIZE_LOOP_WARNING) || documentUrl.startsWith('about:srcdoc')
}

export function scrubUrl(url: string, locators: Locator[]): string {
  const [withoutQuery = ''] = url.split(/[?#]/, 1)
  const [, origin = '', path = ''] = ORIGIN_AND_PATH.exec(withoutQuery) ?? []
  const masked = locators.reduce(
    (current, { pattern, replacement }) => current.replace(new RegExp(pattern), replacement),
    path,
  )
  return `${origin}${masked}`
}

export function maskAddresses(text: string): string {
  return text.replace(EMAIL_ADDRESS, FILTERED)
}

function maskedText(link: object, key: 'name' | 'message' | 'stack'): string {
  const value: unknown = Reflect.get(link, key)
  return typeof value === 'string' ? maskAddresses(value) : ''
}

// A cause chain holds whatever the code threw, so each link arrives untyped and is narrowed here.
export function scrubCause(link: unknown, depth = 0): ScrubbedCause | undefined {
  if (depth >= MAX_CAUSES || typeof link !== 'object' || link === null) return undefined
  return {
    name: maskedText(link, 'name'),
    message: maskedText(link, 'message'),
    stack: maskedText(link, 'stack'),
    cause: scrubCause(Reflect.get(link, 'cause'), depth + 1),
  }
}

export function scrubNotice(notice: ScrubbableNotice, locators: Locator[], referrer: string): void {
  if (notice.url) notice.url = scrubUrl(notice.url, locators)
  notice.message = maskAddresses(notice.message)
  notice.stack = maskAddresses(notice.stack)
  notice.cause = scrubCause(notice.cause)
  notice.cookies = {}
  notice.params = {}
  notice.cgiData = { ...notice.cgiData, HTTP_REFERER: scrubUrl(referrer, locators) }
}

function keptMetadataValue(key: string, value: unknown, locators: Locator[]): unknown {
  if (typeof value === 'string' && BREADCRUMB_URLS.has(key)) return scrubUrl(value, locators)
  if (typeof value === 'string' && BREADCRUMB_TEXTS.has(key)) return maskAddresses(value)
  if ((typeof value === 'string' || typeof value === 'number') && BREADCRUMB_SCALARS.has(key)) {
    return value
  }
  return undefined
}

function scrubRequestMessage(message: string, locators: Locator[]): string {
  const space = message.indexOf(' ')
  if (space < 0) return maskAddresses(message)
  return `${message.slice(0, space)} ${scrubUrl(message.slice(space + 1), locators)}`
}

function pathOf(url: unknown): string {
  return ORIGIN_AND_PATH.exec(String(url))?.[2] ?? ''
}

export function scrubBreadcrumb(breadcrumb: ScrubbableBreadcrumb, locators: Locator[]): boolean {
  if (!BREADCRUMB_CATEGORIES.has(breadcrumb.category)) return false
  if (String(breadcrumb.metadata?.message).startsWith(RESIZE_LOOP_WARNING)) return false

  const metadata = Object.fromEntries(
    Object.entries(breadcrumb.metadata ?? {}).flatMap(([key, value]) => {
      const kept = keptMetadataValue(key, value, locators)
      return kept === undefined ? [] : [[key, kept]]
    }),
  )
  if (breadcrumb.category === 'navigation' && pathOf(metadata.from) === pathOf(metadata.to)) {
    return false
  }

  breadcrumb.metadata = metadata
  breadcrumb.message =
    breadcrumb.category === 'request'
      ? scrubRequestMessage(breadcrumb.message, locators)
      : maskAddresses(breadcrumb.message)
  return true
}
